/**
 * Compact-viewport detection for the deck viewer.
 *
 * Decks are authored at a fixed 1920x1080 16:9 stage with desktop-scale type
 * and viewport-based Tailwind breakpoints. On a phone (either orientation) or
 * a small tablet there is no way to render that natively without clipping. So
 * below a usability threshold the viewer switches to a **reflowed, scrollable**
 * layout: the slide flows to its mobile breakpoints, top-to-bottom, and the
 * viewport scrolls instead of clipping.
 *
 * Desktops and laptops never trip the threshold, so their rendering path is
 * completely unchanged.
 *
 * `computeCompact` is the pure core (exported for tests); `useCompactViewport`
 * wires it to `resize` / `orientationchange`.
 */
import { useEffect, useState } from "react";

/**
 * Below either of these the native 1920x1080 render is unusable, so we reflow.
 * Tuned so no common laptop trips it (smallest is ~1366x768, and 1280x720
 * half-screens stay native) while every phone — including landscape phones,
 * which are wide but short — does.
 */
export const COMPACT_MAX_WIDTH = 1024;
export const COMPACT_MAX_HEIGHT = 700;

/** Pure: is this viewport too small to render the 16:9 stage natively? */
export function computeCompact(
  viewportWidth: number,
  viewportHeight: number,
): boolean {
  return (
    viewportWidth < COMPACT_MAX_WIDTH || viewportHeight < COMPACT_MAX_HEIGHT
  );
}

/**
 * Track the viewport and report whether the viewer should reflow + scroll
 * (compact) rather than render the fixed 16:9 stage. Recomputes on resize and
 * orientation change.
 */
export function useCompactViewport(): boolean {
  const [compact, setCompact] = useState<boolean>(() =>
    typeof window === "undefined"
      ? false
      : computeCompact(window.innerWidth, window.innerHeight),
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const update = () =>
      setCompact(computeCompact(window.innerWidth, window.innerHeight));
    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  return compact;
}

/**
 * Track the viewport pixel size, recomputing on resize / orientation change.
 *
 * Used by the viewer to scale `stageOnCompact` slides (which keep their fixed
 * 16:9 composition on small screens) uniformly to fit the viewport.
 */
export function useViewportSize(): { width: number; height: number } {
  const [size, setSize] = useState<{ width: number; height: number }>(() =>
    typeof window === "undefined"
      ? { width: 1920, height: 1080 }
      : { width: window.innerWidth, height: window.innerHeight },
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const update = () =>
      setSize({ width: window.innerWidth, height: window.innerHeight });
    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  return size;
}
